import React from "react";

import {
  AlertTriangle,
  ChevronRight,
  Lightbulb,
  Loader2,
  MessageCircle,
  MessagesSquare,
  SearchX,
  Sparkles,
} from "lucide-react";

import {
  COV_CLASS,
  CoverageChips,
  SEVERITY_ORDER,
  SeverityChip,
  SlideChip,
  SourceFlag,
  VERDICT_CLASS,
  answerCoverage,
} from "./BriefingParts";
import { FLOW } from "./flowCopy";
import PlanStateLabel, { viewStateOf } from "./PlanStateLabel";
import { ReviewLensToggle } from "./ReviewMarks";
import { ComingSoon, NextStepCard, PlanNav, shortDate } from "./shared";
import { useAccountPlanFile } from "../../Hooks/useAccountPlan";
import type {
  AccountPlanOverview,
  BriefingAnswer,
  PlanBriefing,
} from "../../Types/AccountPlan";

interface Props {
  accountId: string;
  data: AccountPlanOverview;
  nav: PlanNav;
}

const LEGEND_CHIP =
  "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold";

/** Grey bars standing in for content that doesn't exist yet. */
const Skeleton: React.FC<{ widths: string[] }> = ({ widths }) => (
  <div className="space-y-1.5" aria-hidden>
    {widths.map((w, i) => (
      <div key={i} className={`h-2.5 rounded bg-gray-100 ${w}`} />
    ))}
  </div>
);

const Placeholder: React.FC<{ label: string; widths: string[] }> = ({
  label,
  widths,
}) => (
  <div>
    <div className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-400">
      {label}
    </div>
    <Skeleton widths={widths} />
  </div>
);

const Eyebrow: React.FC<{ children: React.ReactNode; cls?: string }> = ({
  children,
  cls = "text-gray-400",
}) => (
  <div
    className={`mb-1.5 font-mono text-[10px] uppercase tracking-widest ${cls}`}
  >
    {children}
  </div>
);

const LINK =
  "rounded text-xs font-semibold text-[#8538d4] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50";

/** A headline fits in the summary pill (mock v4: "Partially — QBRs yes, EBRs no"). */
const HEADLINE_MAX = 48;

/** Mock v4 answer card: summary line, then evidence / interpretation / gap / recommendation. */
const AnswerCard: React.FC<{
  answer: BriefingAnswer;
  index: number;
  open: boolean;
  showAsk: boolean;
  onOpenSlide: (slide: number) => void;
  /** Opens the plan chat about this answer. */
  onAskFollowUp?: () => void;
}> = ({ answer: a, index, open, showAsk, onOpenSlide, onAskFollowUp }) => (
  <details
    open={open}
    data-testid={`qa-card-${index + 1}`}
    className="group rounded-xl border border-gray-200 bg-white shadow-sm"
  >
    <summary className="flex cursor-pointer list-none flex-col gap-2 rounded-xl px-5 py-3.5 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 sm:flex-row sm:items-start sm:justify-between">
      <span className="flex min-w-0 flex-1 items-start gap-2.5">
        <ChevronRight
          className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition group-open:rotate-90"
          aria-hidden
        />
        <span className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-gray-400">
          Q{index + 1}
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold leading-snug text-gray-900">
            {a.question}
          </span>
          {/* Older drafts wrote a sentence here: it reads under the question, not as a pill. */}
          {a.direct_answer.length > HEADLINE_MAX && (
            <span className="mt-1 block text-sm leading-snug text-gray-700">
              {a.direct_answer}
            </span>
          )}
        </span>
      </span>
      <span className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:max-w-[45%]">
        <SeverityChip severity={a.severity_tag} />
        {a.direct_answer.length <= HEADLINE_MAX && (
          <span
            className={`whitespace-nowrap rounded px-2 py-0.5 text-xs font-semibold ${VERDICT_CLASS[a.verdict]}`}
          >
            {a.direct_answer}
          </span>
        )}
        <SourceFlag flag={a.source_flag} />
      </span>
    </summary>
    <div className="space-y-4 border-t border-gray-100 px-5 pb-5 pt-4">
      {/* Product review 2026-11-01: the answer first, the deck's evidence under it. */}
      {a.interpretation && (
        <div>
          <Eyebrow>Interpretation</Eyebrow>
          <p className="text-sm leading-relaxed text-gray-700">
            {a.interpretation}
          </p>
        </div>
      )}
      {a.evidence.length > 0 && (
        <div>
          <Eyebrow>Evidence from deck</Eyebrow>
          <div className="space-y-1.5">
            {a.evidence.map((e, i) => (
              <blockquote
                key={i}
                className="flex items-start gap-2 border-l-2 border-purple-300 py-1 pl-3 text-sm italic text-gray-700"
              >
                <span>&ldquo;{e.quote}&rdquo;</span>
                <SlideChip slide={e.slide} onOpen={onOpenSlide} />
              </blockquote>
            ))}
          </div>
        </div>
      )}
      {a.gap_callout && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <SearchX
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-700"
            aria-hidden
          />
          <div>
            <Eyebrow cls="text-amber-800">
              What&apos;s missing from the plan
            </Eyebrow>
            <p className="text-sm text-amber-900">{a.gap_callout}</p>
          </div>
        </div>
      )}
      {a.recommendation && (
        <div className="flex items-start gap-2 rounded-lg border border-purple-200 bg-purple-50 p-3">
          <Lightbulb
            className="mt-0.5 h-4 w-4 shrink-0 text-[#8538d4]"
            aria-hidden
          />
          <div>
            <Eyebrow cls="text-[#8538d4]">Recommendation</Eyebrow>
            <p className="text-sm text-purple-950">{a.recommendation}</p>
          </div>
        </div>
      )}
      {(onAskFollowUp || (showAsk && answerCoverage(a) !== "answered")) && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {showAsk && answerCoverage(a) !== "answered" && (
            <ComingSoon
              label="Ask the team"
              icon={MessageCircle}
              title="Asking the account team a follow-up comes with virtual sync. For now, raise it in your next sync."
            />
          )}
          {onAskFollowUp && (
            <button
              type="button"
              onClick={onAskFollowUp}
              className="inline-flex items-center gap-1.5 rounded-full border border-purple-200 px-2.5 py-1 text-xs font-semibold text-[#8538d4] hover:border-[#974ae4] hover:bg-purple-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden /> Ask a follow-up
            </button>
          )}
        </div>
      )}
    </div>
  </details>
);

const Answers: React.FC<{
  briefing: PlanBriefing;
  isES: boolean;
  version: number | null;
  planDate?: string | null;
  nav: PlanNav;
}> = ({ briefing, isES, version, planDate, nav }) => {
  const counts: Partial<Record<string, number>> = {};
  briefing.answers.forEach(a => {
    counts[a.severity_tag] = (counts[a.severity_tag] || 0) + 1;
  });
  const openSlide = (n: number) => nav.openSlide(n);

  return (
    <>
      <div
        data-testid="coverage-row"
        className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 shadow-sm"
      >
        <span className="text-xs text-gray-500">
          Answered from plan{version != null ? ` v${version}` : ""}
          {planDate ? ` (${shortDate(planDate)})` : ""}:
        </span>
        <CoverageChips coverage={briefing.coverage} />
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => nav.go("sponsor")}
          className={LINK}
        >
          See account briefing
        </button>
        <span className="text-gray-300" aria-hidden>
          ·
        </span>
        <button type="button" onClick={() => nav.go("viewer")} className={LINK}>
          {FLOW.viewSource}
        </button>
      </div>

      {briefing.bottom_line.text && (
        <div
          data-testid="bottom-line"
          className="rounded-xl border border-gray-200 bg-white px-6 py-5 shadow-sm"
        >
          <Eyebrow>Bottom line · synthesized across all answers</Eyebrow>
          <p className="text-sm leading-relaxed text-gray-800">
            {briefing.bottom_line.text}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {SEVERITY_ORDER.filter(k => counts[k]).map(k => (
              <SeverityChip key={k} severity={k} count={counts[k]} />
            ))}
            {briefing.bottom_line.slides.map(n => (
              <SlideChip key={n} slide={n} onOpen={openSlide} />
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {briefing.answers.map((a, i) => (
          <AnswerCard
            key={a.question_id}
            answer={a}
            index={i}
            // ES: every card starts closed (Sushant, 2026-10-25); AE/CSM review every answer.
            open={!isES}
            showAsk={isES}
            onOpenSlide={openSlide}
            onAskFollowUp={
              nav.askAboutPlan
                ? () =>
                    nav.askAboutPlan?.({
                      prefill: `On Q${i + 1}: `,
                      questionId: a.question_id,
                      label: `Q${i + 1} · ${a.question}`,
                    })
                : undefined
            }
          />
        ))}
      </div>
    </>
  );
};

/**
 * Briefing Questions: the sponsor's step 2 and a lens of Review for AE/CSM. EB's answers to the
 * standard Briefing Questions, each cited to the plan's slides. The answers sit
 * in their own tinted panel with an answered / partly / missing legend (BQ-10),
 * so they read as separate from the briefing.
 */
const QaTab: React.FC<Props> = ({ accountId, data, nav }) => {
  const isES = nav.persona === "ES";
  const qs = data.question_set;
  const questions = qs?.questions || [];
  const {
    file,
    loading,
    error: fileError,
    reload,
  } = useAccountPlanFile(accountId, data.current_file_id);
  const view = file?.sponsor_view;
  const briefing = view?.briefing || null;
  const failed = !briefing && view?.briefing_status === "failed";
  // A briefing generated before EB answered the briefing questions (2026-09-28).
  const predates = !!view && !briefing && !failed;
  const published = !!data.current_review?.published;
  const n = briefing?.answers.length || questions.length;
  const sponsor = data.sponsors[0]?.name;
  const gaps = briefing
    ? briefing.coverage.partial + briefing.coverage.missing
    : 0;

  let body: React.ReactNode;
  if (loading && !file) {
    body = (
      <div className="flex items-center gap-2 py-10 text-sm text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin text-[#974ae4]" aria-hidden />{" "}
        Loading answers…
      </div>
    );
  } else if (briefing) {
    body = (
      <Answers
        briefing={briefing}
        isES={isES}
        // The plan number (D1); sponsor_view.file_version is the internal one.
        version={file?.plan_version ?? null}
        planDate={file?.created_at}
        nav={nav}
      />
    );
  } else if (!file && fileError) {
    // The plan file didn't load: say so, rather than "No answers yet".
    body = (
      <div
        role="alert"
        className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
      >
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
        <span>Couldn&apos;t load the answers.</span>
        <button
          type="button"
          onClick={() => void reload()}
          className="font-medium text-red-800 underline underline-offset-2"
        >
          Try again
        </button>
      </div>
    );
  } else if (failed && isES) {
    // The sponsor is told plainly that the answers couldn't be generated — a
    // deliberate exception to "no failure wording for sponsors" (INV-06), decided
    // for QA-02 (Sushant, 2026-10-19). No retry steps or internal workflow: those
    // are the team's (below).
    body = (
      <div
        role="status"
        data-testid="qa-sponsor-unavailable"
        className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700"
      >
        <MessagesSquare
          className="mt-0.5 h-4 w-4 shrink-0 text-gray-500"
          aria-hidden
        />
        <span>
          The answers to these questions couldn&apos;t be generated for this
          plan.
        </span>
      </div>
    );
  } else if (failed) {
    body = (
      <div
        role="alert"
        className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        <AlertTriangle
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-700"
          aria-hidden
        />
        <span>
          <span className="font-semibold">
            The Briefing Questions couldn&apos;t be answered for this draft.
          </span>{" "}
          {nav.canAct("AE")
            ? "Use Write the briefing again on Review briefing to try again."
            : "The AE can regenerate the draft briefing to try again."}
        </span>
      </div>
    );
  } else {
    body = (
      <>
        <div className="flex items-start gap-2 rounded-lg border border-purple-100 bg-purple-50/60 px-4 py-3 text-sm text-purple-900">
          <MessagesSquare
            className="mt-0.5 h-4 w-4 shrink-0 text-[#974ae4]"
            aria-hidden
          />
          {predates ? (
            <span>
              <span className="font-semibold">
                This briefing was {published ? "published" : "generated"} before
                EB answered the Briefing Questions.
              </span>{" "}
              {published
                ? "Answers come with the next plan version the AE uploads."
                : nav.canAct("AE")
                  ? "Use Write the briefing again on Review briefing to add them."
                  : "They appear when the AE regenerates the draft briefing."}
            </span>
          ) : (
            <span>
              <span className="font-semibold">No answers yet.</span> EB answers
              each question from the plan when the draft briefing is generated,
              with evidence quoted from the slides and what the plan
              doesn&apos;t cover.
            </span>
          )}
        </div>
        <div className="space-y-3">
          {questions.map((q, i) => (
            <details
              key={q.id}
              className="group rounded-xl border border-gray-200 bg-white shadow-sm"
            >
              <summary className="flex cursor-pointer list-none items-center gap-3 rounded-xl p-4 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50">
                <ChevronRight
                  className="h-4 w-4 shrink-0 text-gray-400 transition group-open:rotate-90"
                  aria-hidden
                />
                <span className="shrink-0 font-mono text-xs font-semibold text-[#974ae4]">
                  Q{i + 1}
                </span>
                <span className="min-w-0 flex-1 text-sm font-medium text-gray-900">
                  {q.text}
                </span>
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                  Awaiting answer
                </span>
              </summary>
              <div className="space-y-4 border-t border-gray-100 px-5 py-4 opacity-70">
                <Placeholder
                  label="Interpretation"
                  widths={["w-full", "w-10/12", "w-7/12"]}
                />
                <Placeholder
                  label="Evidence from deck"
                  widths={["w-11/12", "w-9/12"]}
                />
                <Placeholder
                  label="What's missing from the plan"
                  widths={["w-8/12"]}
                />
                <span className="sr-only">Not generated yet.</span>
              </div>
            </details>
          ))}
        </div>
      </>
    );
  }

  return (
    <div className="space-y-4">
      {!isES && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <ReviewLensToggle active="questions" nav={nav} />
        </div>
      )}

      <section
        data-testid="bq-panel"
        aria-label={FLOW.briefingQuestions}
        className="space-y-4 rounded-2xl border border-purple-200 bg-purple-50/40 p-4 md:p-5"
      >
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {FLOW.briefingQuestions}
            <span className="ml-2 text-sm font-normal text-gray-500">
              {isES
                ? `Your ${n} questions`
                : `${sponsor ? `${sponsor}'s` : "The sponsor's"} ${n} questions`}
            </span>
          </h2>
          {qs && (
            <p className="mt-0.5 text-xs text-gray-500">
              Set by {qs.label} · updated {shortDate(qs.updated_at)}
            </p>
          )}
          {isES && data.current_file_id && (
            // The answers come from the newest version: draft until published (Q1 = C + A).
            <PlanStateLabel
              className="mt-2"
              variant="banner"
              state={viewStateOf(data.current_review?.published)}
              publishedAt={data.current_review?.published?.at}
            />
          )}
        </div>

        <div
          data-testid="bq-legend"
          className="flex flex-wrap items-center gap-2 text-xs text-gray-500"
        >
          <span>Each answer is marked:</span>
          <span className={`${LEGEND_CHIP} ${COV_CLASS.answered}`}>
            Answered
          </span>
          <span className={`${LEGEND_CHIP} ${COV_CLASS.partial}`}>Partly</span>
          <span className={`${LEGEND_CHIP} ${COV_CLASS.missing}`}>
            Missing from the plan
          </span>
        </div>

        {body}
      </section>

      {isES ? (
        <NextStepCard
          label="Next step"
          action={{
            label: FLOW.viewSource,
            onClick: () => nav.go("viewer"),
          }}
        >
          Every answer cites its slide. Open the source to check the evidence
          behind any answer.
        </NextStepCard>
      ) : (
        <NextStepCard
          label="Next step"
          action={{
            label: "Go to Review briefing",
            onClick: () => nav.go("sponsor"),
          }}
        >
          Back to the briefing to finish your review.
          {nav.persona === "AE" && gaps > 0 && (
            <span className="text-gray-500">
              {" "}
              {gaps} answer{gaps === 1 ? "" : "s"} flag the plan as incomplete —
              capture them for the next plan version.
            </span>
          )}
        </NextStepCard>
      )}
    </div>
  );
};

export default QaTab;
